import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { find as _find, forEach as _forEach, findIndex as _findIndex } from 'lodash';
import moment from 'moment';

import { colorOptionFormatter } from 'components/forms/autocomplete/Color';
import { getNameInCurrentLanguage } from 'helpers/intl';

import FormInput from 'components/forms/FormInput';
import FormLangInput from 'components/forms/FormLangInput';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import FormLangAutocomplete from 'components/forms/FormLangAutocomplete';
import FormRichEditor from 'components/forms/FormRichEditor';
import FormLangRichEditor from 'components/forms/FormLangRichEditor';
import FormCheckbox from 'components/forms/FormCheckbox';
import FormRadio from 'components/forms/FormRadio';
import DatePicker from 'components/forms/DatePicker';
import { Button } from 'react-bootstrap';
import ModalFlyerAlarmOptions from 'components/modals/ModalFlyerAlarmOptions';
import FlyerAlarmOptionsSelector from './FlyerAlarmOptionsSelector';

class ItemAttribute extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showFlyerAlarmModal: false,
    };
  }

  hideFlyerAlarmModal = () => {
    this.setState({ showFlyerAlarmModal: false });
  };

  // does this attribute consist of one or multiple values?
  getValueQuantity = () => {
    const { attribute } = this.props;

    switch (attribute.type) {
      case 'checkbox':
        return 'multi';
    }

    return 'single';
  };

  getValueId = () => {
    const { entity, attribute } = this.props;
    const entityAttributes = entity.getAttributes();
    const entityAttribute = _find(entityAttributes, ['id', attribute.id]);

    if (entityAttribute) {
      if (this.getValueQuantity() !== 'multi') {
        return entityAttribute.getValues()[0].getId();
      }
    }

    return null;
  };

  // get value from entity, without the name or attribute id
  getValue = standard => {
    const { entity, attribute } = this.props;
    const entityAttributes = entity.getAttributes();
    const entityAttribute = _find(entityAttributes, ['id', attribute.id]);
    const isMultilanguage = attribute.is_multilanguage;

    if (entityAttribute) {
      if (this.getValueQuantity() === 'multi') {
        const values = [];
        _forEach(entityAttribute.getValues(), value => {
          values.push(value.getId());
        });
        return values;
      }

      switch (attribute.type) {
        case 'colors':
        case 'select':
          if (standard) {
            let isStandard = null;
            _forEach(attribute.values, value => {
              if (value.standard) {
                isStandard = value.id;
              }
            });
            return isStandard;
          }
          if (isMultilanguage) {
            const option = entityAttribute.getValues()[0];
            let label = '';
            _forEach(option.value, langName => {
              if (langName.name !== undefined) {
                label += langName.name + ' ';
              }
            });
            return { ...option, langValue: option.value, value: option.id, label };
          }

          return entityAttribute.getValues()[0].getId();
      }

      if (isMultilanguage) {
        return entityAttribute.getValues()[0].getValue();
      }

      return entityAttribute
        .getValues()[0]
        .getValue()[0]
        .getName();
    }

    return null;
  };

  handleChange = (name, values) => {
    const { entity, attribute, changeEntity } = this.props;
    const entityAttributes = entity.getAttributes().slice();
    const entityAttribute = _find(entityAttributes, ['id', attribute.id]);
    const index = _findIndex(entityAttributes, ['id', attribute.id]);

    let newAttribute;
    if (index >= 0) {
      const newValues = []; // we have the attribute id as name
      _forEach(values, value => {
        // we can iterate the values
        const entityAttributeValue = _find(entityAttribute.getValues(), ['id', value.id]); // check if the value is present in the current entity
        const attributeValue = _find(attribute.values, ['id', value.id]); // check if the value is present in the attribute values list

        if (entityAttributeValue) {
          // if the value is present, we can construct new array of multilanguage names
          const newNames = [];
          _forEach(value.value, formLangValue => {
            // formLangValue comes from the form (values parameter)
            let found = false;
            _forEach(entityAttributeValue.getValue(), langValue => {
              // names from entity attribute
              // check if there are some values in the entity attribute
              // point here is to make sure the IDs match (so that the preUpdate events on the backend fire correctly)
              if (langValue.getLang() === formLangValue.lang) {
                found = true;

                newNames.push({
                  id: langValue.getId(),
                  lang: formLangValue.lang,
                  name: formLangValue.name,
                });
              }
            });

            if (!found) {
              newNames.push({
                lang: formLangValue.lang,
                name: formLangValue.name,
              });
            }
          });

          newValues.push({
            id: value.id,
            value: newNames,
            color: value.color,
          });
        } else if (attributeValue) {
          const newNames = [];
          _forEach(value.value, formLangValue => {
            // formLangValue comes from the form (values parameter)
            let found = false;
            _forEach(attributeValue.value, langValue => {
              // name from the attribute values definition
              if (langValue.lang === formLangValue.lang) {
                found = true;

                newNames.push({
                  id: langValue.id,
                  lang: formLangValue.lang,
                  name: formLangValue.name,
                });
              }
            });

            if (!found) {
              newNames.push({
                lang: formLangValue.lang,
                name: formLangValue.name,
              });
            }
          });

          newValues.push({
            id: value.id,
            value: newNames,
            color: value.color,
          });
        } else {
          newValues.push(value);
        }
      });

      newAttribute = entity.createValue('attributes', [], {
        id: attribute.id,
        name: attribute.name,
        values: newValues,
      });
    } else {
      newAttribute = entity.createValue('attributes', [], {
        id: attribute.id,
        name: attribute.name,
        values,
      });
    }

    if (index >= 0) {
      entityAttributes.splice(index, 1, newAttribute);
    } else {
      entityAttributes.push(newAttribute);
    }
    const newEntity = { ...entity.updateValue('attributes', entityAttributes, []) };
    changeEntity(newEntity);
  };

  getMetaInformation = () => {
    const { attribute, entity } = this.props;
    if (!attribute.is_required) {
      return null;
    }

    const meta = { validate: { rules: 'required' }, acl: { read: true, write: true } };
    let validation;

    const errorIndex = _findIndex(entity.errors, ['fieldName', 'attribute_' + attribute.id]);
    if (errorIndex > -1) {
      validation = { valid: false, helpText: entity.errors[errorIndex].validationResult.helpText };
    }

    return { meta, validation };
  };

  getElement = () => {
    const {
      intl: { messages },
      appIntl,
      attribute,
    } = this.props;

    const { showFlyerAlarmModal } = this.state;

    const label = getNameInCurrentLanguage(attribute.name, appIntl);
    const isMultilanguage = attribute.is_multilanguage;
    const isCustomFlyerAlarmOptions = attribute.is_custom_flyer_alarm_options;

    switch (attribute.type) {
      case 'select': {
        // select - user may select one value from a list
        if (isMultilanguage) {
          return (
            <FormLangAutocomplete
              onChange={(name, value) =>
                this.handleChange(name, [{ id: value.id, value: value.langValue, color: null }])
              }
              name={attribute.id}
              value={this.getValue()}
              defaultValue={this.getValue(true)}
              label={label}
              autocompleteType="attribute"
              options={attribute.values.map(option => {
                let optionLabel = '';
                _forEach(option.value, langName => {
                  if (langName.name !== undefined) {
                    optionLabel += langName.name + ' ';
                  }
                });
                return { ...option, langValue: option.value, value: option.id, label: optionLabel };
              })}
              {...this.getMetaInformation()}
            />
          );
        }

        return (
          <FormAutocomplete
            onChange={(name, value) =>
              this.handleChange(name, [
                { id: value, value: [{ lang: null, name: this.getSelectLabel(value) }], color: null },
              ])
            }
            name={attribute.id}
            value={this.getValue()}
            defaultValue={this.getValue(true)}
            label={label}
            options={this.getSelectOptions()}
            {...this.getMetaInformation()}
          />
        );
      }

      case 'html': {
        // select - user may enter one value as html, using rich form editor
        if (isMultilanguage) {
          return (
            <FormLangRichEditor
              onChange={(name, value) => this.handleChange(name, [{ id: this.getValueId(), value, color: null }])}
              name={attribute.id}
              value={this.getValue()}
              label={label}
              langKey="name"
              {...this.getMetaInformation()}
            />
          );
        }

        return (
          <FormRichEditor
            onChange={(name, value) =>
              this.handleChange(name, [{ id: this.getValueId(), value: [{ lang: null, name: value }], color: null }])
            }
            name={attribute.id}
            value={this.getValue()}
            label={label}
            {...this.getMetaInformation()}
          />
        );
      }

      case 'checkbox': {
        if (isMultilanguage) {
          return (
            <FormCheckbox
              onChange={(name, values) =>
                this.handleChange(
                  name,
                  values.map(value => {
                    return { id: value, value: this.getCheckboxValue(value), color: null };
                  })
                )
              }
              name={attribute.id}
              values={this.getValue()}
              options={this.getSelectOptions()}
              label={label}
              isMultilanguage
              {...this.getMetaInformation()}
            />
          );
        }

        return (
          <FormCheckbox
            onChange={(name, values) =>
              this.handleChange(
                name,
                values.map(value => {
                  return { id: value, value: [{ lang: null, name: this.getSelectLabel(value) }], color: null };
                })
              )
            }
            name={attribute.id}
            values={this.getValue()}
            options={this.getSelectOptions()}
            label={label}
            {...this.getMetaInformation()}
          />
        );
      }

      case 'text': {
        if (isMultilanguage) {
          return (
            <FormLangInput
              onChange={(name, value) => this.handleChange(name, [{ id: this.getValueId(), value, color: null }])}
              name={attribute.id}
              value={this.getValue()}
              label={label}
              {...this.getMetaInformation()}
            />
          );
        }

        if (isCustomFlyerAlarmOptions) {
          // special case of a complex FlyerAlarm attribute
          const { id } = attribute;
          const { entity } = this.props;
          const vendorId = entity?.getIdent?.()?.getVendorId?.();
          const itemCountries = entity?.getItemCountry?.();

          const setValue = name => {
            this.handleChange(id, [{ id: this.getValueId(), value: [{ id: null, lang: null, name }], color: null }]);
          };

          const handleSelect = value => {
            this.hideFlyerAlarmModal();
            setValue(value);
          };

          const faModal = (
            <ModalFlyerAlarmOptions show={showFlyerAlarmModal} onHide={this.hideFlyerAlarmModal}>
              <FlyerAlarmOptionsSelector
                value={this.getValue()}
                handleSelect={handleSelect}
                countries={itemCountries}
                vendorId={vendorId}
              />
            </ModalFlyerAlarmOptions>
          );

          const suffixAddon = (
            <>
              <Button variant="primary" onClick={() => this.setState({ showFlyerAlarmModal: true })}>
                FA
              </Button>
            </>
          );

          return (
            <>
              {faModal}
              <FormInput
                onChange={(name, value) =>
                  this.handleChange(name, [
                    { id: this.getValueId(), value: [{ id: null, lang: null, name: value }], color: null },
                  ])
                }
                name={attribute.id}
                value={this.getValue()}
                label={label}
                {...this.getMetaInformation()}
                suffixAddon={suffixAddon}
              />
            </>
          );
        }

        return (
          <FormInput
            onChange={(name, value) =>
              this.handleChange(name, [
                { id: this.getValueId(), value: [{ id: null, lang: null, name: value }], color: null },
              ])
            }
            name={attribute.id}
            value={this.getValue()}
            label={label}
            {...this.getMetaInformation()}
          />
        );
      }

      case 'colors': {
        // colors exist only as not multilanguage
        return (
          <FormLangAutocomplete
            onChange={(name, value) =>
              this.handleChange(name, [{ id: value.id, value: value.langValue, color: value.color }])
            }
            name={attribute.id}
            value={this.getValue()}
            label={label}
            autocompleteType="color"
            options={colorOptionFormatter(attribute.values)}
            {...this.getMetaInformation()}
          />
        );
      }

      case 'number': {
        if (isMultilanguage) {
          return (
            <FormLangInput
              type="number"
              onChange={(name, value) => this.handleChange(name, [{ id: this.getValueId(), value, color: null }])}
              name={attribute.id}
              value={this.getValue()}
              label={label}
              {...this.getMetaInformation()}
            />
          );
        }

        return (
          <FormInput
            type="number"
            onChange={(name, value) =>
              this.handleChange(name, [
                { id: this.getValueId(), value: [{ id: null, lang: null, name: value }], color: null },
              ])
            }
            name={attribute.id}
            value={this.getValue()}
            label={label}
            {...this.getMetaInformation()}
          />
        );
      }

      case 'date': {
        const dateValue = this.getValue();

        return (
          <DatePicker
            onChange={(name, value) =>
              this.handleChange(name, [{ id: this.getValueId(), value: [{ lang: null, name: value }], color: null }])
            }
            name={attribute.id}
            value={dateValue || moment()}
            label={label}
            {...this.getMetaInformation()}
          />
        );
      }

      case 'boolean': {
        return (
          <FormRadio
            label={label}
            options={[{ label: messages.yes, value: 'yes' }, { label: messages.no, value: 'no' }]}
            onChange={(name, value) => {
              this.handleChange(name, [{ id: this.getValueId(), value: [{ lang: null, name: value }], color: null }]);
            }}
            name={attribute.id}
            value={this.getValue() ? this.getValue() : 'no'}
            {...this.getMetaInformation()}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  // get options for single language select box (label + value) keypair
  getSelectOptions = () => {
    const options = [];
    _forEach(this.props.attribute.values, value => {
      options.push({
        label: getNameInCurrentLanguage(value.value, this.props.appIntl),
        value: value.id,
      });
    });
    return options;
  };

  // get label for a value in single select box
  getSelectLabel = selectedValue => {
    let res = null;
    _forEach(this.props.attribute.values, value => {
      if (value.id === selectedValue) {
        res = getNameInCurrentLanguage(value.value, this.props.appIntl);
      }
    });
    return res;
  };

  // get value (in all languages for given value id)
  getCheckboxValue = valueId => {
    const attributeValue = _find(this.props.attribute.values, ['id', valueId]);

    return attributeValue.value;
  };

  render() {
    const { attribute } = this.props;

    return attribute ? this.getElement() : null;
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
  };
};

export default injectIntl(connect(mapStateToProps)(ItemAttribute));
